/**
 * ProductCare custom element
 * Displays care/maintenance information for selected product options
 * Subscribes to variant selection changes via PubSub
 */

class ProductCare extends HTMLElement {
  constructor() {
    super();
    this.careData = {};
    this.sectionId = null;
    this.unsubscriber = null;
    this.container = null;
    this.contentContainer = null;
  }

  connectedCallback() {
    // Get section ID
    this.sectionId = this.dataset.sectionId;
    if (!this.sectionId) {
      console.error('ProductCare: Missing section ID');
      return;
    }

    // Get DOM elements
    this.container = this.querySelector('.product-care__accordion');
    this.contentContainer = this.querySelector('.product-care__content-wrapper');

    if (!this.container || !this.contentContainer) {
      console.error('ProductCare: Missing required DOM elements');
      return;
    }

    // Parse care data
    try {
      const careDataRaw = this.dataset.careData;
      this.careData = JSON.parse(careDataRaw);
    } catch (e) {
      console.error('ProductCare: Failed to parse care data', e);
      this.careData = {};
    }

    // Subscribe to variant selection changes
    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.handleVariantChange.bind(this));
    }

    // Listen for dorure selection changes
    this.setupDorureListener();

    // Check initial state
    this.updateDisplay();
  }

  /**
   * Set up listener for dorure picker changes
   */
  setupDorureListener() {
    const dorurePicker = document.getElementById(`DorurePicker-${this.sectionId}`);
    if (dorurePicker) {
      dorurePicker.addEventListener('change', (event) => {
        if (event.target.matches('[data-dorure-option]')) {
          this.updateDisplay();
        }
      });
    }
  }

  disconnectedCallback() {
    if (this.unsubscriber) {
      this.unsubscriber();
    }
  }

  /**
   * Handle variant selection change event
   * @param {Object} event - The event data from PubSub
   */
  handleVariantChange(event) {
    // Only respond to changes in our section
    const variantSelects = event?.data?.target?.closest('variant-selects');
    if (!variantSelects || !variantSelects.id.includes(this.sectionId)) {
      return;
    }

    this.updateDisplay();
  }

  /**
   * Get all currently selected option values
   * @returns {Array<{value: string, optionName: string, isDorure: boolean}>}
   */
  getSelectedValues() {
    const selectedValues = [];

    // Get variant values
    const variantSelects = document.getElementById(`variant-selects-${this.sectionId}`);
    if (variantSelects) {
      // Get values from radio inputs (swatch selections)
      const radioInputs = variantSelects.querySelectorAll('input[type="radio"]:checked');
      radioInputs.forEach(input => {
        const fieldset = input.closest('fieldset');
        const legend = fieldset?.querySelector('.form__label');
        const optionName = legend?.textContent?.trim() || '';
        
        selectedValues.push({
          value: input.value.trim(),
          optionName: optionName,
          isDorure: false
        });
      });

      // Get values from select dropdowns
      const selectElements = variantSelects.querySelectorAll('select');
      selectElements.forEach(select => {
        const selectedOption = select.options[select.selectedIndex];
        const label = select.closest('.product-form__input')?.querySelector('label');
        const optionName = label?.textContent?.trim() || '';
        
        if (selectedOption) {
          selectedValues.push({
            value: selectedOption.value.trim(),
            optionName: optionName,
            isDorure: false
          });
        }
      });
    }

    // Get dorure selection
    const dorurePicker = document.getElementById(`DorurePicker-${this.sectionId}`);
    if (dorurePicker) {
      const selectedDorure = dorurePicker.querySelector('input[type="radio"]:checked');
      if (selectedDorure) {
        selectedValues.push({
          value: selectedDorure.value.trim(),
          optionName: 'Dorure',
          isDorure: true
        });
      }
    }

    return selectedValues;
  }

  /**
   * Find care instructions for selected values
   * @returns {Array<{value: string, entretien: string}>}
   */
  findMatchedCareInstructions() {
    const selectedValues = this.getSelectedValues();
    const matched = [];

    for (const selected of selectedValues) {
      // Build the key - dorure values are prefixed with "dorure:"
      const key = selected.isDorure ? `dorure:${selected.value}` : selected.value;
      
      // Check if we have care data for this value
      const careInfo = this.careData[key];
      if (careInfo && careInfo.entretien) {
        matched.push({
          value: selected.value,
          optionName: careInfo.optionName,
          entretien: careInfo.entretien
        });
      }
    }

    return matched;
  }

  /**
   * Update the display based on current selections
   */
  updateDisplay() {
    const matchedCareInstructions = this.findMatchedCareInstructions();

    if (matchedCareInstructions.length > 0) {
      // Clear and update content
      this.contentContainer.innerHTML = '';

      // Add care content for each matched value
      matchedCareInstructions.forEach(item => {
        const itemDiv = document.createElement('div');
        itemDiv.className = 'product-care__item';
        itemDiv.setAttribute('data-option', item.optionName);
        itemDiv.setAttribute('data-value', item.value);
        
        const contentDiv = document.createElement('div');
        contentDiv.className = 'product-care__content';
        contentDiv.innerHTML = item.entretien;
        
        itemDiv.appendChild(contentDiv);
        this.contentContainer.appendChild(itemDiv);
      });

      // Show container
      this.container.style.display = '';
    } else {
      // Hide container
      this.container.style.display = 'none';
    }
  }
}

// Define the custom element
customElements.define('product-care', ProductCare);
